/**
 * Log a quote request that came in by email, phone or in person (2026-09-24, Callum:
 * "record and report on that going forward across all companies and divisions").
 *
 * The one thing this asks that nothing else knows is WHEN it arrived — so the response
 * report can measure from there. Defaults to now; set it earlier for the email that has
 * been sitting since this morning. Logging it is picking it up (the logger is assigned and
 * the pick-up clock stops) unless "somebody else will handle it" is ticked, which leaves
 * it in the Leads inbox to be given out.
 */
import { escapeHtml as esc, getIcon, isoToLocalInput, localInputToISO, showError, showModal, showSuccess } from '@isg/ui-kit';
import { salesApi } from '../services/sales-api.js';
import { accountPicker, newId, saving, segment, wireSegment } from './log-sheet.js';

const CHANNELS = [['email', 'Email', 'mail'], ['phone', 'Phone', 'phone'], ['visit', 'In person', 'map-pin'], ['other', 'Other', 'message-square']];

export function openRequestSheet(account = null, { onSaved } = {}) {
  const id = newId();
  const state = { account, channel: 'email' };
  const content = document.createElement('div');
  content.innerHTML = `
    <p style="margin:0 0 var(--space-3);font-size:var(--text-sm);color:var(--text-secondary)">
      A request for a quote that did not come through the website. Recording when it arrived is what lets us
      measure how quickly it was answered.</p>
    <div class="form-group"><label class="form-label">How it came in</label>${segment('channel', CHANNELS, 'email')}</div>
    <div class="form-group"><label class="form-label" for="rq-when">When it arrived</label>
      <input class="form-input" type="datetime-local" id="rq-when" style="max-width:260px" value="${isoToLocalInput(new Date().toISOString())}">
      <div style="font-size:12px;color:var(--text-tertiary);margin-top:4px">The time the email landed or the phone rang — not now, if it has been waiting.</div></div>
    <div class="form-group"><label class="form-label">From</label><div data-account></div>
      <input class="form-input" data-company maxlength="200" placeholder="…or the company, if they are not an account yet" style="margin-top:6px"></div>
    <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:var(--space-2)">
      <div class="form-group"><label class="form-label">Contact</label><input class="form-input" data-contact maxlength="200"></div>
      <div class="form-group"><label class="form-label">Email</label><input class="form-input" type="email" data-email maxlength="255"></div>
      <div class="form-group"><label class="form-label">Phone</label><input class="form-input" data-phone maxlength="60"></div>
    </div>
    <div class="form-group"><label class="form-label" for="rq-what">What they asked for</label>
      <textarea class="form-textarea" id="rq-what" rows="3" maxlength="5000" placeholder="Parts and quantities, as they asked"></textarea></div>
    <label style="display:flex;gap:var(--space-2);align-items:center;font-size:var(--text-sm)">
      <input type="checkbox" data-someone-else> Somebody else will handle it — leave it in the Leads inbox to be given out</label>`;
  const modal = showModal({ title: 'Log a quote request', content, size: 'md', actions: [
    { label: 'Cancel', className: 'btn btn-secondary' },
    { label: 'Log it', className: 'btn btn-primary', onClick: (close) => save(close) },
  ] });
  accountPicker(content, account, a => { state.account = a; });
  wireSegment(content, 'channel', v => { state.channel = v || 'email'; });

  let busy = false;
  async function save(close) {
    if (busy) return;
    const company = content.querySelector('[data-company]').value.trim();
    if (!state.account && !company) { showError('Pick the account, or type the company it came from.'); return; }
    const when = content.querySelector('#rq-when').value;
    const body = {
      id, entity: state.account ? state.account.entity : undefined, channel: state.channel,
      received_at: when ? localInputToISO(when) : undefined,
      customer_code: state.account ? state.account.code : undefined,
      company_name: company || undefined,
      contact_name: content.querySelector('[data-contact]').value.trim() || undefined,
      contact_email: content.querySelector('[data-email]').value.trim() || undefined,
      contact_phone: content.querySelector('[data-phone]').value.trim() || undefined,
      what: content.querySelector('#rq-what').value.trim() || undefined,
      take_it: !content.querySelector('[data-someone-else]').checked,
    };
    busy = true; saving(modal, true, 'Log it');
    try {
      const res = await salesApi.createRequest(body);
      close();
      showSuccess(body.take_it ? 'Logged — it is yours, and the clock has stopped on picking it up.'
        : 'Logged — it is in the Leads inbox to be given out.');
      window.dispatchEvent(new CustomEvent('sales:work-changed', { detail: {} }));
      if (onSaved) onSaved(res.data);
    } catch (err) {
      showError(err.message || 'The request was not logged.');
      saving(modal, false, 'Log it');
    } finally { busy = false; }
  }
  return modal;
}

/** The QUOTED stamp for a request answered in QX: its number and when it was sent out. */
export function openQuotedSheet(item, onDone) {
  const content = document.createElement('div');
  content.innerHTML = `
    <p style="margin:0 0 var(--space-3);font-size:var(--text-sm);color:var(--text-secondary)">
      Quoted in QX? Give the quote number. If it turns into an order, the ERP tells us — nobody types "won".</p>
    <div class="form-group"><label class="form-label">QX quote number</label>
      <input class="form-input" data-ref maxlength="50" placeholder="e.g. Q.623816"></div>
    <div class="form-group"><label class="form-label">When it was quoted</label>
      <input class="form-input" type="datetime-local" data-when style="max-width:260px" value="${isoToLocalInput(new Date().toISOString())}"></div>`;
  const modal = showModal({ title: `Quoted: ${esc(item.company || 'request')}`, content, size: 'sm', actions: [
    { label: 'Cancel', className: 'btn btn-secondary' },
    { label: 'Save', className: 'btn btn-primary', onClick: async (close) => {
      const ref = content.querySelector('[data-ref]').value.trim();
      if (!ref) { showError('Give the QX quote number.'); return; }
      const when = content.querySelector('[data-when]').value;
      saving(modal, true, 'Save');
      try {
        await salesApi.markRequestQuoted(item.ref, { system: 'qx', ref, quoted_at: when ? localInputToISO(when) : undefined });
        close(); showSuccess('Recorded as quoted.');
        if (onDone) onDone();
      } catch (err) { showError(err.message || 'Not saved.'); saving(modal, false, 'Save'); }
    } }] });
  setTimeout(() => content.querySelector('[data-ref]').focus(), 50);
  return modal;
}

export const REQUEST_ICON = getIcon('mail', 14);
